import { PureComponent, type ReactElement } from "react";
import { TimeAgo } from "widgets";

import { RelativeUserLabel } from "@/modules/users/components/RelativeUserLabel.js";

import { pluralizeWord } from "../../app-utils/string.js";
import { type RawAttributeDefault, type SampleType, type SearchResult, type User } from "../../graphql.js";
import { Checkbox } from "../core/forms/Checkbox.js";
import ModifySampleTypeDialog from "./ModifySampleTypeDialog.js";

export interface ISampleTypeItemProps {
  user: User;
  sampleType: SampleType;
  isSelected: boolean;

  onItemSelectionChange: (sampleType: SampleType, selected: boolean) => void;
  listAllSamples: (sampleType: SampleType) => Promise<SearchResult>;
  fetchFullSampleTypeWithAttributeDefaults: (
    sampleTypeId: number,
  ) => Promise<{ sampleType: SampleType; attributeDefaults: RawAttributeDefault[] }>;
}

interface ISampleTypeItemState {
  showModifySampleTypeDialog?: boolean;
}

class SampleTypeItem extends PureComponent<ISampleTypeItemProps, ISampleTypeItemState> {
  constructor(props) {
    super(props);

    this.state = {
      showModifySampleTypeDialog: false,
    };
  }

  handleItemSelectionChange = (event: any): void => {
    this.props.onItemSelectionChange(this.props.sampleType, event.target.checked);
  };

  handleModifySampleType = () => {
    this.setState({ showModifySampleTypeDialog: true });
  };

  render(): ReactElement {
    const { id, name, description, creator, created, attributeCount, sampleCount, deletedSampleCount, playbooks } =
      this.props.sampleType;
    const playbooksCount = playbooks.length;

    return (
      <li
        className={"saved-item clearfix " + (this.props.isSelected ? "item-row-selected" : "")}
        key={`sample-type-admin-${id}`}
      >
        <div className="item-checkbox-wrapper">
          <div className="item-checkbox checkbox check-default">
            <Checkbox
              id={`saved-item-checkbox-${id}`}
              checked={this.props.isSelected}
              onChange={this.handleItemSelectionChange}
            />
          </div>
        </div>

        <div className="item-desc-wrapper">
          <div className="admin-tools-item-title">
            <a onClick={this.handleModifySampleType} title="Modify this sample type">
              {name}
            </a>{" "}
            <span className="item-count">
              ({attributeCount} {pluralizeWord("attribute", attributeCount)})
            </span>{" "}
            <span className="item-updated">
              saved by <RelativeUserLabel user={creator} /> <TimeAgo dateTimeISO={created} />
            </span>
          </div>
          <p>{description}</p>
        </div>

        <div className="item-label-wrapper">
          <div className="list-item-label">
            <a onClick={() => this.props.listAllSamples(this.props.sampleType)}>
              Used for {sampleCount} active and {deletedSampleCount} deleted samples, configured for {playbooksCount}{" "}
              {pluralizeWord("playbook", playbooksCount)}
            </a>
          </div>
        </div>

        {this.state.showModifySampleTypeDialog && (
          <ModifySampleTypeDialog
            title="Modify sample type"
            sampleType={this.props.sampleType}
            fetchFullSampleTypeWithAttributeDefaults={this.props.fetchFullSampleTypeWithAttributeDefaults}
            onApply={() => this.setState({ showModifySampleTypeDialog: false })}
            onClose={() => this.setState({ showModifySampleTypeDialog: false })}
          />
        )}
      </li>
    );
  }
}

export default SampleTypeItem;
